import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

const H2 = ({children}) => <h2 className="font-display text-2xl mt-10 mb-3 tracking-tight">{children}</h2>;
const P = ({children}) => <p className="text-muted-foreground leading-relaxed">{children}</p>;

export default function Terms() {
  const [legal, setLegal] = useState(null);
  useEffect(() => { api.get('/legal/config').then(r => setLegal(r.data)).catch(() => {}); }, []);
  const owner = legal?.owner_name || 'AgriNexus';
  const contact = legal?.contact_email || 'info@agrinexus.it';
  const isBeta = legal?.beta_mode;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="terms-page">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Documenti legali</div>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter">Termini di servizio</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Versione {legal?.terms_version || '2'} · Ultimo aggiornamento: 2 ottobre 2026
      </p>

      {isBeta && (
        <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-muted-foreground">
          <strong className="text-foreground">Versione beta privata.</strong> Il servizio è offerto gratuitamente, "così com'è", senza garanzie di continuità o disponibilità. Potremmo modificare funzioni o sospendere il servizio senza preavviso durante questa fase.
        </div>
      )}

      <H2>1. Chi siamo e cosa facciamo</H2>
      <P>
        {owner} gestisce AgriNexus, una <strong>bacheca di annunci</strong> dedicata alla compravendita e all'affitto di terreni agricoli in Italia. Mettiamo in contatto proprietari e interessati fornendo gli strumenti tecnici (pubblicazione annunci, ricerca, messaggistica, assistente AI).
      </P>
      <P>
        <strong>Non siamo mediatori immobiliari</strong> ai sensi della L. 39/1989: non siamo iscritti al RUI, non interveniamo nelle trattative, non percepiamo provvigioni sulle compravendite che avvengono tra gli utenti, non verifichiamo la veridicità delle informazioni pubblicate (eccetto il controllo del documento d'identità del "Proprietario Verificato"). Operiamo come fornitore di servizi della società dell'informazione ("hosting attivo", D.Lgs. 70/2003 artt. 14-17).
      </P>

      <H2>2. Chi può usare il servizio</H2>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground list-disc pl-5">
        <li>Devi avere almeno 14 anni (richiesto dal GDPR per prestare validamente il consenso al trattamento in Italia).</li>
        <li>Devi fornire dati veri e aggiornati.</li>
        <li>Un account per persona. Account multipli di spam/frode saranno chiusi senza preavviso.</li>
      </ul>

      <H2>3. Pubblicazione annunci</H2>
      <P>Pubblicando un annuncio dichiari e garantisci che:</P>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground list-disc pl-5">
        <li>Sei il proprietario, usufruttuario, affittuario autorizzato, o rappresenti legalmente il titolare del diritto.</li>
        <li>Le informazioni fornite (superficie, prezzo, dotazioni, foto) sono veritiere e aggiornate.</li>
        <li>Hai titolo per concedere licenze d'uso delle foto che carichi (sei tu l'autore o hai permesso dell'autore).</li>
        <li>Non pubblichi contenuti illegali, discriminatori, offensivi, contraffatti o che violano diritti di terzi.</li>
      </ul>
      <P>Ci riserviamo di rimuovere senza preavviso annunci che violino queste regole e, in caso di violazione grave, chiudere l'account.</P>

      <H2>4. Il badge "Proprietario Verificato"</H2>
      <P>
        Il badge attesta <strong>solo</strong> che un amministratore ha visionato un documento d'identità confrontandolo con il profilo. Non garantisce la veridicità delle informazioni dell'annuncio né la bontà della trattativa. Il badge può essere revocato in qualsiasi momento in caso di segnalazioni comprovate.
      </P>

      <H2>5. Trattative e contratti tra utenti</H2>
      <P>
        Qualsiasi accordo di compravendita o affitto è <strong>esclusivamente tra gli utenti</strong>: AgriNexus non è parte del contratto, non percepisce acconti o caparre, non detiene fondi, non emette fatture per conto degli utenti. Consigliamo sempre di: fare un sopralluogo fisico, verificare la visura catastale e lo stato urbanistico presso l'Agenzia del Territorio, consultare un notaio prima del rogito, un commercialista per gli aspetti fiscali e un agronomo per quelli colturali.
      </P>

      <H2>6. Contenuto generato da AI</H2>
      <P>
        L'assistente AI "Giulio", le valutazioni "Stima Quintali", le descrizioni auto-generate e qualsiasi altro contenuto prodotto dall'AI del sito sono forniti <strong>a titolo puramente informativo</strong>. Non costituiscono consulenza agronomica, legale, fiscale o immobiliare professionale. Le stime di resa, di valore e di idoneità colturale sono probabilistiche e vanno sempre verificate con un professionista qualificato prima di qualsiasi decisione economica.
      </P>

      <H2>7. Responsabilità</H2>
      <P>
        Nei limiti massimi consentiti dalla legge, {owner} non è responsabile:
      </P>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground list-disc pl-5">
        <li>Dell'accuratezza delle informazioni pubblicate dagli utenti.</li>
        <li>Dell'esito delle trattative, inclusi inadempimenti contrattuali tra utenti.</li>
        <li>Di danni derivanti dall'uso o dall'impossibilità di usare il servizio.</li>
        <li>Di perdite indirette, mancato guadagno, danni d'immagine.</li>
      </ul>
      <P>Finché il servizio è gratuito, la nostra responsabilità verso ciascun utente è limitata, per ciascun evento, a quanto l'utente ha effettivamente pagato (zero in beta gratuita). Restano fermi i diritti inderogabili del consumatore ai sensi del Codice del Consumo.</P>

      <H2>8. Comportamenti vietati</H2>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground list-disc pl-5">
        <li>Spam, phishing, truffe, catena di Sant'Antonio.</li>
        <li>Scraping automatico, uso di bot, reverse engineering.</li>
        <li>Pubblicazione di dati personali altrui senza consenso.</li>
        <li>Discorsi d'odio, molestie, discriminazioni.</li>
        <li>Qualsiasi tentativo di aggirare la sicurezza, i limiti o la moderazione del sito.</li>
      </ul>
      <P>La violazione comporta sospensione o chiusura immediata dell'account e, nei casi previsti, segnalazione alle autorità competenti.</P>

      <H2>9. Rimozione contenuti e segnalazioni</H2>
      <P>
        Ai sensi del D.Lgs. 70/2003 artt. 16-17, interveniamo tempestivamente alla rimozione di contenuti illeciti di cui veniamo a conoscenza. Chiunque può segnalarci un contenuto illecito scrivendo a <a className="underline" href={`mailto:${contact}`}>{contact}</a> indicando l'URL, la natura della violazione e i propri recapiti.
      </P>

      <H2>10. Proprietà intellettuale</H2>
      <P>
        Il nome, il logo, il design e il codice di AgriNexus sono di proprietà del titolare. I contenuti caricati dagli utenti restano degli utenti stessi, che tuttavia concedono a {owner} una licenza non esclusiva, gratuita e revocabile all'atto della cancellazione dell'annuncio, per pubblicarli nel sito e nei canali di promozione del servizio.
      </P>

      <H2>11. Chiusura dell'account</H2>
      <P>Puoi chiudere il tuo account in qualsiasi momento dalla dashboard. {owner} può chiudere l'account di un utente in caso di violazione grave di questi Termini, previo preavviso quando possibile.</P>

      <H2>12. Modifiche</H2>
      <P>Possiamo modificare questi Termini per adeguarci a nuove normative, nuove funzioni del servizio o esigenze operative. Le modifiche sostanziali ti saranno comunicate via email con almeno 15 giorni di anticipo e richiederanno l'accettazione al login successivo.</P>

      <H2>13. Legge applicabile e foro competente</H2>
      <P>
        Questi Termini sono regolati dalla <strong>legge italiana</strong>. Per gli utenti consumatori il foro competente è quello di residenza o domicilio del consumatore, se in Italia. In via alternativa, ai sensi del Reg. UE 524/2013, puoi ricorrere alla piattaforma ODR della Commissione Europea: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="underline">ec.europa.eu/consumers/odr</a>.
      </P>

      <H2>14. Contatti</H2>
      <P>Scrivi a <a className="underline" href={`mailto:${contact}`}>{contact}</a> per qualsiasi comunicazione formale.</P>

      <div className="mt-12 flex gap-3 text-sm">
        <Link to="/privacy" className="underline text-muted-foreground hover:text-foreground">Informativa privacy →</Link>
        <Link to="/cookie" className="underline text-muted-foreground hover:text-foreground">Cookie policy →</Link>
      </div>
    </div>
  );
}
